import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { StateService } from "../../../services/state.service";
import { s25Resource } from "../../../util/s25.resource";
import { LoginStore } from "../../../stores/login.store";
import type { DropDownItem } from "../../../pojo/DropDownItem";
import {
    ElectionApi,
    type PositionResultDto,
    type VotingOpenDto,
    type WinnerRowDto,
    type WinnersDto,
} from "./election.api";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25RouterLinkDirective } from "../../s25-nav-bar/s25.router.link.directive";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { S25TableModule } from "../../s25-table/s25.table.module";
import { Table } from "../../s25-table/Table";

@TypeManagerDecorator("s25-ng-election-results-page")
@Component({
    selector: "s25-ng-election-results-page",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        S25ButtonComponent,
        S25RouterLinkDirective,
        S25DropdownPaginatedModule,
        S25TableModule,
    ],
    host: { class: "election-results-page" },
    template: `
        <div class="c-padding--double">
            <p class="c-margin-bottom--single">
                <a s25-ng-router-link [stateName]="'seElectionAdmin'" class="aw-link">← Back to list</a>
            </p>
            <h1>Election Results</h1>
            @if (!login.loggedIn()) {
                <p>Sign in to view results.</p>
            } @else {
                <div class="election-results__election-block c-margin-bottom--double">
                    <div class="election-results__filter-row">
                        <label class="election-results__field-label">Election</label>
                        <s25-ng-dropdown-search-criteria
                            class="c-input"
                            style="display: inline-block; min-width: 16rem; vertical-align: middle"
                            [type]="'elections'"
                            [value]="selectedElectionDropDown()"
                            (valueChange)="onElectionDropDownChange($event)"
                        />
                        <s25-ng-button [type]="'outline'" [onClick]="refreshAll">Refresh</s25-ng-button>
                    </div>
                    @if (
                        selectedElectionId() != null &&
                        !votingOpenResource.isLoading() &&
                        votingOpenResource.value()?.votingOpen
                    ) {
                        <div class="c-alert c-alert--warning c-margin-top--single" role="status">
                            Voting is still open for this election; results can change until voting ends
                            @if (votingOpenResource.value()?.votingEndDt; as votingEndDateRaw) {
                                on {{ votingEndDateRaw | date: "medium" }}.
                            } @else {
                                .
                            }
                        </div>
                    }
                </div>
                <label class="c-margin-bottom--single" style="display: block">
                    <input
                        type="checkbox"
                        [ngModel]="showWinnersTable()"
                        (ngModelChange)="showWinnersTable.set($event)"
                    />
                    Show overall winners table
                </label>
                @if (selectedElectionId() != null) {
                    @if (showWinnersTable()) {
                        <section class="c-margin-top--double">
                            <h2>Winners</h2>
                            @if (winnersSectionOrgName()) {
                                <p class="c-text--secondary">{{ winnersSectionOrgName() }}</p>
                            }
                            @if (winnersResource.isLoading()) {
                                <p>Loading…</p>
                            } @else if (winnersResource.error()) {
                                <p class="c-text--error">{{ winnersResource.error() }}</p>
                            } @else {
                                <s25-ng-table [dataSource]="winnersTable()"></s25-ng-table>
                            }
                        </section>
                    } @else {
                        <section class="c-margin-top--double">
                            <h2>Per-Position Results</h2>
                            <div class="election-results__filter-row c-margin-bottom--single">
                                <label class="election-results__field-label">Position</label>
                                @for (electionId of positionCriteriaRemountByElectionId(); track electionId) {
                                    <s25-ng-dropdown-search-criteria
                                        class="c-input"
                                        style="display: inline-block; min-width: 16rem; vertical-align: middle"
                                        [type]="'electionPositionsForElection'"
                                        [customFilterValue]="electionId.toString()"
                                        [value]="selectedPositionDropDown()"
                                        (valueChange)="onPositionDropDownChange($event)"
                                    />
                                }
                            </div>
                            @if (positionBarSectionOrgName()) {
                                <p class="c-text--secondary c-margin-bottom--single">
                                    {{ positionBarSectionOrgName() }}
                                </p>
                            }
                            @if (positionBarResultsResource.isLoading()) {
                                <p>Loading…</p>
                            } @else if (positionBarResultsResource.error()) {
                                <p class="c-text--error">{{ positionBarResultsResource.error() }}</p>
                            } @else if (positionBarResultRows().length === 0) {
                                <p>No votes for this position yet.</p>
                            } @else {
                                <div class="election-bar-chart">
                                    @for (barRow of positionBarResultRows(); track barRow.contId) {
                                        <div class="election-bar-row">
                                            <div class="election-bar-label">
                                                {{ barRow.name }}
                                                @if (barRow.email) {
                                                    <span class="c-text--secondary"> ({{ barRow.email }})</span>
                                                }
                                            </div>
                                            <div class="election-bar-track">
                                                <div
                                                    class="election-bar-fill"
                                                    [style.width.%]="positionBarFillWidthPercent(barRow.voteCount)"
                                                ></div>
                                            </div>
                                            <div class="election-bar-count">{{ barRow.voteCount }}</div>
                                        </div>
                                    }
                                </div>
                            }
                        </section>
                    }
                }
            }
        </div>
    `,
    styles: `
        .election-results__filter-row {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.5rem 1.25rem;
        }
        .election-results__field-label {
            margin: 0;
        }
        .election-results__election-block {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 0;
        }
        :host ::ng-deep .election-winners__tied-detail {
            white-space: pre-line;
            line-height: 1.4;
            max-width: 32rem;
        }
        .election-bar-chart {
            max-width: 720px;
        }
        .election-bar-row {
            display: grid;
            grid-template-columns: 1fr 2fr auto;
            gap: 0.5rem 1rem;
            align-items: center;
            margin-bottom: 0.75rem;
        }
        .election-bar-label {
            font-size: 0.9rem;
        }
        .election-bar-track {
            background: #eee;
            border-radius: 4px;
            height: 22px;
            overflow: hidden;
        }
        .election-bar-fill {
            background: #3a6ea5;
            height: 100%;
            min-width: 2px;
            border-radius: 4px;
        }
        .election-bar-count {
            text-align: right;
            min-width: 2rem;
        }
    `,
})
export class ElectionResultsPageComponent implements OnInit {
    readonly login = inject(LoginStore);

    /** Bumps when the per-position bar chart should refetch (position/election change or Refresh). */
    private readonly positionBarRequestGeneration = signal(0);
    /** Bumps when voting-open / winners data should refetch (Refresh). */
    private readonly votingAndWinnersRequestGeneration = signal(0);

    readonly selectedElectionDropDown = signal<DropDownItem<number> | null>(null);
    readonly selectedElectionId = computed(() => {
        const chosen = this.selectedElectionDropDown();
        const rawId = chosen?.itemId ?? chosen?.val;
        if (rawId == null) {
            return null;
        }
        const asNumber = Number(rawId);
        return Number.isFinite(asNumber) ? asNumber : null;
    });

    /**
     * One element, tracked by election id, so the position {@code s25-ng-dropdown-search-criteria}
     * is recreated when the election changes. That component only loads options in {@code ngOnInit}
     * (including when {@code type} changes), not when {@code customFilterValue} alone changes.
     */
    readonly positionCriteriaRemountByElectionId = computed((): number[] => {
        const electionId = this.selectedElectionId();
        return electionId != null ? [electionId] : [];
    });

    readonly selectedPositionDropDown = signal<DropDownItem<number> | null>(null);
    readonly selectedPositionId = computed(() => {
        const chosen = this.selectedPositionDropDown();
        const rawId = chosen?.itemId ?? chosen?.val;
        if (rawId == null) {
            return null;
        }
        const asNumber = Number(rawId);
        return Number.isFinite(asNumber) ? asNumber : null;
    });
    readonly showWinnersTable = signal(false);

    readonly positionBarResultsResource = s25Resource({
        if: () => this.selectedElectionId() != null && this.selectedPositionId() != null && !this.showWinnersTable(),
        params: () => ({
            electionId: this.selectedElectionId()!,
            positionId: this.selectedPositionId()!,
            positionBarRequestGeneration: this.positionBarRequestGeneration(),
        }),
        loader: async ({ params }) =>
            ElectionApi.getPositionResults(params.electionId, params.positionId) as Promise<PositionResultDto>,
    });

    readonly votingOpenResource = s25Resource({
        if: () => this.selectedElectionId() != null,
        params: () => ({
            electionId: this.selectedElectionId()!,
            votingAndWinnersRequestGeneration: this.votingAndWinnersRequestGeneration(),
        }),
        loader: async ({ params }) => ElectionApi.getVotingOpen(params.electionId) as Promise<VotingOpenDto>,
    });

    readonly winnersResource = s25Resource({
        if: () => this.selectedElectionId() != null && this.showWinnersTable(),
        params: () => ({
            electionId: this.selectedElectionId()!,
            votingAndWinnersRequestGeneration: this.votingAndWinnersRequestGeneration(),
        }),
        loader: async ({ params }) => ElectionApi.getWinners(params.electionId) as Promise<WinnersDto>,
    });

    ngOnInit(): void {
        const storedState = StateService.getState();
        const routeParams = storedState?.params as { electionId?: string } | undefined;
        const electionIdFromRoute = routeParams?.electionId ? Number(routeParams.electionId) : NaN;
        if (!Number.isNaN(electionIdFromRoute) && electionIdFromRoute > 0) {
            this.selectedElectionDropDown.set({
                itemId: electionIdFromRoute,
                itemName: `Election ${electionIdFromRoute}`,
            });
        }
    }

    readonly positionBarResultRows = computed(() => this.positionBarResultsResource.value()?.rows ?? []);
    readonly positionBarSectionOrgName = computed(() => this.positionBarResultsResource.value()?.orgName || "");
    readonly winnersSectionOrgName = computed(() => this.winnersResource.value()?.orgName || "");

    private readonly maxPositionBarVoteCount = computed(() => {
        const rows = this.positionBarResultRows();
        return Math.max(1, ...rows.map((barRow) => barRow.voteCount));
    });

    positionBarFillWidthPercent(voteCount: number): number {
        return (voteCount / this.maxPositionBarVoteCount()) * 100;
    }

    readonly winnersTable = computed((): Table.DataSource => {
        const winnersDto = this.winnersResource.value();
        const rowOrder = [...(winnersDto?.rows ?? [])].sort(
            (leftRow, rightRow) => (leftRow.positionSortOrder ?? 0) - (rightRow.positionSortOrder ?? 0),
        );
        const tableRows: Table.Row[] = rowOrder.map((winnerRow, rowIndex) => ({
            id: rowIndex,
            name: winnerRow.winnerName || winnerRow.positionTitle,
            cells: {
                positionTitle: { text: winnerRow.positionTitle },
                winnerName: { text: winnerRow.winnerName },
                winnerEmail: { text: winnerRow.winnerEmail },
                votes: { text: String(winnerRow.winnerVoteCount ?? "") },
                tie: { text: winnerRow.tie ? "Yes" : "" },
                tiedDetail: {
                    className: "election-winners__tied-detail",
                    text: formatTiedCandidatesList(winnerRow),
                },
            },
        }));
        return {
            type: "unpaginated",
            dataSource: () => Promise.resolve({ rows: tableRows }),
            columns: [
                { id: "positionTitle", header: "Position" },
                { id: "winnerName", header: "Winner" },
                { id: "winnerEmail", header: "Email" },
                { id: "votes", header: "Votes" },
                { id: "tie", header: "Tie" },
                { id: "tiedDetail", header: "Ties" },
            ],
        };
    });

    onElectionDropDownChange(chosen: DropDownItem<number> | null | undefined) {
        this.selectedElectionDropDown.set(chosen ?? null);
        this.selectedPositionDropDown.set(null);
        this.positionBarRequestGeneration.update((priorGeneration) => priorGeneration + 1);
    }

    onPositionDropDownChange(chosen: DropDownItem<number> | null | undefined) {
        this.selectedPositionDropDown.set(chosen ?? null);
        this.positionBarRequestGeneration.update((priorGeneration) => priorGeneration + 1);
    }

    refreshAll = () => {
        this.positionBarRequestGeneration.update((priorGeneration) => priorGeneration + 1);
        this.votingAndWinnersRequestGeneration.update((priorGeneration) => priorGeneration + 1);
    };
}

function formatTiedCandidatesList(winnerRow: WinnerRowDto): string {
    const list = winnerRow.tiedCandidates;
    if (!list || list.length === 0) {
        return "";
    }
    const voteNote =
        winnerRow.winnerVoteCount != null && winnerRow.winnerVoteCount > 0
            ? `${winnerRow.winnerVoteCount} votes each\n`
            : "";
    return (
        voteNote +
        list
            .map((candidate) => {
                const emailPart = candidate.email ? ` (${candidate.email})` : "";
                return `${candidate.name}${emailPart}`;
            })
            .join("\n")
    );
}
